import { moodFromSlider } from "./moods";
import type { NearbyPlace, SocialContext, UserKit } from "./types";
import {
  INDOOR_KINDS,
  OUTDOOR_KINDS,
  PLACE_KIND_LABELS,
  SPEED_KMH,
  type SettingPref,
  type TravelMode,
} from "./plans";
import { PAID_KINDS as CATALOG_PAID } from "./placeCatalog";
import { fetchNearbyPlacesCascade } from "./places";
import { estimatedCostEur, fitsBudget, fitsTime, visitMinutesFor } from "./planFilters";
import { categoriesOfPlace, filterPlacesByCategories, interleaveByCategory, type PlaceCategoryId } from "./categories";

export type PlanActivityTipo = "adrenalina" | "calma" | "meditativo" | "social" | "creativo" | "energetico" | "otro";

export type PlanSearchInput = {
  lat: number;
  lng: number;
  transporte: TravelMode;
  /** Tiempo libre total en horas (o se deriva de minutes). */
  tiempoLibreHoras?: number;
  minutes?: number;
  setting?: SettingPref;
  /** Categorías de actividad marcadas por el usuario (vacío o ausente = todas). */
  categories?: PlaceCategoryId[];
  /** @deprecated usa maxBudgetEur = 0. */
  freeOnly?: boolean;
  /** Presupuesto máximo por persona en € (null/undefined = sin límite, 0 = gratis). */
  maxBudgetEur?: number | null;
  social: SocialContext;
  /** Mood slider 0–1 → tipo psicológico. */
  mood?: number;
  tipo?: PlanActivityTipo;
  kit?: UserKit;
  /** POIs ya cargados (opcional; si faltan se ingesta). */
  places?: NearbyPlace[];
  /** Eventos inventados por IA: desactivado por defecto (la IA no conoce la agenda real). */
  aiEvents?: boolean;
};

export type RankedPlanItem = {
  id: string;
  nombre: string;
  tipo_actividad: string;
  puntuacion_calculada: number;
  coordenadas: { lat: number; lng: number };
  gratis: boolean;
  /** Coste orientativo por persona (€). */
  precio_estimado: number;
  /** Minutos de estancia que caben en el tiempo libre. */
  minutos_estimados: number;
  razon_de_seleccion: string;
  /** Categorías de actividad a las que pertenece el sitio. */
  categorias?: PlaceCategoryId[];
};

export type PlanSearchResult = {
  radioMaximoKm: number;
  tiempoIdaHoras: number;
  placesIngested: number;
  eventsAdded: number;
  afterFilters: number;
  /** Sitios descartados por presupuesto o por no dar tiempo. */
  discardedByBudget: number;
  discardedByTime: number;
  /** Sitios descartados por no ser de ninguna categoría marcada. */
  discardedByCategory: number;
  items: RankedPlanItem[];
};

const PAID_KINDS = CATALOG_PAID;

const GROUP_KINDS = new Set(["bowling", "kart", "nightclub", "ski", "bungee", "parachuting", "paintball", "circus"]);
const ADRENALINA_KINDS = new Set([
  "kart",
  "bungee",
  "ski",
  "sport",
  "mountain",
  "bridge",
  "parachuting",
  "climbing",
  "paintball",
  "theme_park",
  "water_park",
]);
const CALMA_KINDS = new Set([
  "library",
  "park",
  "cave",
  "museum",
  "gallery",
  "church",
  "viewpoint",
  "garden",
  "spa",
  "historic",
  "monastery",
]);
const SOCIAL_FORCE_ZERO_SOLO = new Set(["bowling", "kart", "nightclub", "circus", "paintball"]);

function clamp01to5(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(5, n));
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export function tiempoLibreHorasFromInput(input: PlanSearchInput): number {
  if (input.tiempoLibreHoras != null && input.tiempoLibreHoras > 0) return input.tiempoLibreHoras;
  const minutes = input.minutes ?? 90;
  return Math.max(0.25, minutes / 60);
}

/**
 * TiempoIda (h) = (TiempoLibre × 0.4) / 2
 * RadioMaximo (km) = V × TiempoIda
 */
export function computeRadioMaximoKm(transporte: TravelMode, tiempoLibreHoras: number): {
  tiempoIdaHoras: number;
  radioMaximoKm: number;
  radioMaximoMeters: number;
} {
  const tiempoIdaHoras = (Math.max(0.25, tiempoLibreHoras) * 0.4) / 2;
  const v = SPEED_KMH[transporte] ?? 4;
  const idealKm = Math.max(0.4, v * tiempoIdaHoras);
  const radioMaximoMeters = Math.round(Math.min(45000, Math.max(800, idealKm * 1000)));
  // La interfaz muestra «radio X km»: debe ser el radio con el que se busca de verdad.
  const radioMaximoKm = radioMaximoMeters / 1000;
  return { tiempoIdaHoras, radioMaximoKm, radioMaximoMeters };
}

export function tipoFromMood(mood: number | undefined, override?: PlanActivityTipo): PlanActivityTipo {
  if (override) return override;
  const label = moodFromSlider(mood ?? 0.5).label.toLowerCase();
  if (label.includes("adrenalina")) return "adrenalina";
  if (label.includes("meditativo")) return "meditativo";
  if (label.includes("relaj") || label.includes("cansado")) return "calma";
  if (label.includes("social")) return "social";
  if (label.includes("creativo")) return "creativo";
  if (label.includes("energ") || label.includes("aventur")) return "energetico";
  return "otro";
}

export function normalizeBaseRating(place: NearbyPlace): number {
  if (place.isEvent) return place.rating != null ? clamp01to5(place.rating) : 4;
  if (place.rating != null && Number.isFinite(place.rating)) return clamp01to5(place.rating);
  return 2.5;
}

export function isPaidPlace(place: NearbyPlace): boolean {
  if (place.paid === true) return true;
  if (place.paid === false) return false;
  if (place.isEvent && place.paid == null) return false;
  return PAID_KINDS.has(place.kind);
}

/** Exclusiones booleanas estrictas (paso 4). */
export function applyBooleanExclusions(
  places: NearbyPlace[],
  opts: {
    setting?: SettingPref;
    freeOnly?: boolean;
    maxBudgetEur?: number | null;
    freeMinutes?: number;
    transporte?: TravelMode;
  },
): NearbyPlace[] {
  const setting = opts.setting ?? "any";
  const budget = effectiveBudget(opts);
  return places.filter((place) => {
    const kind = place.kind;
    if (setting === "indoor") {
      const outdoorStrict = new Set([
        "park",
        "plaza",
        "square",
        "mountain",
        "viewpoint",
        "bridge",
        "cave",
        "marketplace",
        "monument",
        "castle",
        "ruins",
        "attraction",
        "ski",
        "bungee",
      ]);
      if (outdoorStrict.has(kind)) return false;
      if (OUTDOOR_KINDS.has(kind) && !INDOOR_KINDS.has(kind)) return false;
    }
    if (setting === "outdoor") {
      if (kind === "bar" && place.outdoorSeating) {
        /* terraza OK */
      } else if (
        kind === "bar" ||
        kind === "restaurant" ||
        kind === "museum" ||
        kind === "library" ||
        kind === "bowling" ||
        kind === "cinema" ||
        kind === "theatre" ||
        kind === "gallery" ||
        kind === "nightclub" ||
        kind === "kart" ||
        (INDOOR_KINDS.has(kind) && !OUTDOOR_KINDS.has(kind))
      ) {
        return false;
      }
    }
    if (budget != null && !fitsBudget(place, budget)) return false;
    if (budget === 0 && isPaidPlace(place)) return false;
    if (opts.freeMinutes != null && opts.transporte && !fitsTime(place, opts.freeMinutes, opts.transporte)) return false;
    return true;
  });
}

/** Presupuesto efectivo: maxBudgetEur manda; freeOnly (antiguo) equivale a 0 €. */
export function effectiveBudget(opts: { freeOnly?: boolean; maxBudgetEur?: number | null }): number | null {
  if (opts.maxBudgetEur != null && Number.isFinite(opts.maxBudgetEur)) return Math.max(0, opts.maxBudgetEur);
  return opts.freeOnly ? 0 : null;
}

function hobbyTokens(kit?: UserKit): string[] {
  return (kit?.hobbies || [])
    .map((h) =>
      String(h || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase(),
    )
    .filter(Boolean);
}

function placeBlob(place: NearbyPlace): string {
  return `${place.name} ${place.kind} ${(place.tags || []).join(" ")} ${place.address}`
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/** MatchFiltros 0–5 (TIPO + COMPAÑÍA). */
export function scoreMatchFiltros(
  place: NearbyPlace,
  opts: { tipo: PlanActivityTipo; social: SocialContext },
): number {
  let score = 2.5;
  const kind = place.kind;

  if (opts.tipo === "adrenalina") {
    if (ADRENALINA_KINDS.has(kind) || place.isEvent) score = 5;
    else if (kind === "museum" || kind === "library") score = 0;
    else score = 1.5;
  } else if (opts.tipo === "calma" || opts.tipo === "meditativo") {
    if (CALMA_KINDS.has(kind)) score = 5;
    else if (kind === "nightclub" || kind === "kart" || kind === "bowling") score = 0;
    else score = 2;
  } else if (opts.tipo === "energetico") {
    score = ADRENALINA_KINDS.has(kind) || kind === "park" || kind === "sport" ? 4.5 : 2.2;
  } else if (opts.tipo === "creativo") {
    score =
      kind === "gallery" ||
      kind === "museum" ||
      kind === "viewpoint" ||
      kind === "park" ||
      kind === "marketplace" ||
      kind === "arts_centre" ||
      kind === "artwork" ||
      kind === "historic"
        ? 4.8
        : 2.3;
  } else if (opts.tipo === "social") {
    score =
      GROUP_KINDS.has(kind) ||
      kind === "bar" ||
      kind === "cafe" ||
      kind === "plaza" ||
      kind === "mall" ||
      kind === "cinema" ||
      kind === "circus" ||
      kind === "fast_food" ||
      kind === "mcdonalds" ||
      kind === "burgerking"
        ? 4.5
        : 2.5;
  }

  if (opts.social === "solo") {
    if (SOCIAL_FORCE_ZERO_SOLO.has(kind) && opts.tipo !== "social") {
      score = Math.min(score, 0.4);
    }
    if (kind === "monument" || kind === "park" || kind === "mountain" || kind === "library" || kind === "museum") {
      score = Math.max(score, 4.2);
    }
  } else if (opts.social === "amigos" || opts.social === "familia" || opts.social === "familia_extendida") {
    if (GROUP_KINDS.has(kind) || kind === "restaurant" || kind === "cinema") score = Math.max(score, 4.3);
  }

  return clamp01to5(score);
}

/** MatchPerfil 0–5 según hobbies. */
export function scoreMatchPerfil(place: NearbyPlace, kit?: UserKit): number {
  const hobbies = hobbyTokens(kit);
  if (!hobbies.length) return 2.5;
  const blob = placeBlob(place);
  let hits = 0;
  let weight = 0;
  for (const hobby of hobbies) {
    let local = 0;
    if (/canto|musica|música|karaoke|concierto/.test(hobby)) {
      if (/concierto|musica|karaoke|teatro|theatre|music_venue|nightclub|bar|event/.test(blob) || place.isEvent) local = 5;
    } else if (/acuarela|dibujo|foto|pintura|arte/.test(hobby)) {
      if (/mirador|viewpoint|park|plaza|gallery|museum|artwork/.test(blob)) local = 5;
      else if (/bar|bowling|kart/.test(blob)) local = 0.5;
    } else if (/deporte|futbol|running|bici|ciclismo|escalada|ski|padel/.test(hobby)) {
      if (/sport|stadium|pitch|ski|mountain|park|kart|bowling/.test(blob)) local = 5;
    } else if (/lectura|libro|leer|escribir/.test(hobby)) {
      if (/library|books|park|cafe|museum/.test(blob)) local = 4.8;
    } else if (/senderismo|trekking|monta[ñn]ismo|naturaleza|excursi/.test(hobby)) {
      if (/mountain|park|nature_reserve|forest|viewpoint|waterfall|cave/.test(blob)) local = 5;
    } else if (/cocina|gastronomia|vino/.test(hobby)) {
      if (/restaurant|cafe|bar|marketplace|bakery/.test(blob)) local = 4.5;
    } else if (/cine|pelicula|film|series/.test(hobby)) {
      if (/cinema|theatre|film_location|historic|museum/.test(blob)) local = 5;
    } else if (/compra|moda|ropa|shopping|muebles|brico|ferreter/.test(hobby)) {
      if (/clothes|furniture|hardware|doityourself|mall|shop|variety|marketplace/.test(blob)) local = 5;
    } else if (/animal|zoo|mascota/.test(hobby)) {
      if (/zoo|aquarium|circus|pet|dog_park|farm/.test(blob)) local = 5;
    } else if (/historia|patrimonio|castillo/.test(hobby)) {
      if (/historic|castle|ruins|monument|memorial|archaeological|monastery|church/.test(blob)) local = 5;
    } else if (blob.includes(hobby.slice(0, Math.min(8, hobby.length)))) {
      local = 3.8;
    }
    if (local > 0) {
      hits += 1;
      weight += local;
    }
  }
  if (!hits) return 2.2;
  return clamp01to5(weight / hits);
}

export function scoreBonusEventos(place: NearbyPlace): number {
  return place.isEvent ? 5 : 1.5;
}

export function computeNotaFinal(
  place: NearbyPlace,
  opts: { tipo: PlanActivityTipo; social: SocialContext; kit?: UserKit },
): { nota: number; matchFiltros: number; matchPerfil: number; bonusEventos: number; notaBase: number } {
  const notaBase = normalizeBaseRating(place);
  const matchFiltros = scoreMatchFiltros(place, opts);
  const matchPerfil = scoreMatchPerfil(place, opts.kit);
  const bonusEventos = scoreBonusEventos(place);
  const nota = notaBase * 0.2 + matchFiltros * 0.4 + matchPerfil * 0.3 + bonusEventos * 0.1;
  return {
    nota: round2(clamp01to5(nota)),
    matchFiltros,
    matchPerfil,
    bonusEventos,
    notaBase,
  };
}

function buildReason(
  place: NearbyPlace,
  scores: ReturnType<typeof computeNotaFinal>,
  opts: { tipo: PlanActivityTipo; social: SocialContext; kit?: UserKit },
): string {
  const bits: string[] = [];
  const label = PLACE_KIND_LABELS[place.kind] || place.kind;
  bits.push(`${label} cerca (${place.distanceMeters != null ? `${place.distanceMeters} m` : "zona"})`);
  if (place.isEvent) bits.push("evento de hoy (+bonus)");
  if (scores.matchFiltros >= 4) bits.push(`encaja con tipo ${opts.tipo} y compañía ${opts.social}`);
  else if (scores.matchFiltros <= 1) bits.push("poco alineado con el tipo elegido");
  const hobbies = (opts.kit?.hobbies || []).slice(0, 3);
  if (hobbies.length && scores.matchPerfil >= 3.5) {
    bits.push(`conecta con hobbies: ${hobbies.join(", ")}`);
  }
  const cost = isPaidPlace(place) ? estimatedCostEur(place) : 0;
  bits.push(cost > 0 ? `unos ${cost} € por persona` : "gratis");
  return bits.join(". ") + ".";
}

/** Eventos locales vía LLM si no hay suficientes (paso 3). */
export async function fetchEventsViaLlm(input: {
  lat: number;
  lng: number;
  dateIso: string;
}): Promise<NearbyPlace[]> {
  const prompt = `Lista exactamente 5 eventos casuales o culturales REALISTAS para HOY (${input.dateIso}) cerca de lat=${input.lat}, lng=${input.lng} (España). Responde SOLO JSON array: [{"nombre":"...","tipo":"concierto|mercadillo|partido|cultura|otro","lat":n,"lng":n,"gratis":true}]. Coordenadas a <8 km del punto.`;
  try {
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), 20000) : null;
    const response = await fetch("https://rycso.es/api/argos/ai/assist?app=hermes", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        model: "openai",
        messages: [
          { role: "system", content: "Devuelve solo JSON válido, sin markdown." },
          { role: "user", content: prompt },
        ],
      }),
      signal: controller?.signal,
    });
    if (timer) clearTimeout(timer);
    if (!response.ok) return [];
    const data = (await response.json()) as {
      choices?: Array<{ message?: { content?: string } }>;
      content?: string;
    };
    const text = String(data.choices?.[0]?.message?.content || data.content || "");
    const start = text.indexOf("[");
    const end = text.lastIndexOf("]");
    if (start < 0 || end <= start) return [];
    const parsed = JSON.parse(text.slice(start, end + 1)) as Array<Record<string, unknown>>;
    return (parsed || [])
      .filter((row) => row && row.nombre)
      .slice(0, 8)
      .map((row, index) => {
        const lat = Number(row.lat ?? input.lat);
        const lng = Number(row.lng ?? input.lng);
        const tipo = String(row.tipo || "cultura");
        const kind =
          /concierto|musica/.test(tipo) ? "theatre" : /mercado|mercadillo/.test(tipo) ? "marketplace" : /partido|deporte/.test(tipo) ? "sport" : "attraction";
        return {
          id: `event-llm-${index}-${Date.now()}`,
          name: String(row.nombre),
          kind,
          address: "Evento hoy",
          latitude: lat,
          longitude: lng,
          rating: 4,
          paid: row.gratis === false,
          isEvent: true,
          tags: ["evento", tipo],
        } satisfies NearbyPlace;
      });
  } catch {
    return [];
  }
}

export function rankPlacesToOutput(
  places: NearbyPlace[],
  opts: {
    tipo: PlanActivityTipo;
    social: SocialContext;
    kit?: UserKit;
    limit?: number;
    freeMinutes?: number;
    transporte?: TravelMode;
    categories?: PlaceCategoryId[];
  },
): RankedPlanItem[] {
  const limit = opts.limit ?? 20;
  const scored = places
    .map((place) => {
      const scores = computeNotaFinal(place, opts);
      return {
        place,
        scores,
        item: {
          id: place.id,
          nombre: place.name,
          tipo_actividad: PLACE_KIND_LABELS[place.kind] || place.kind,
          puntuacion_calculada: scores.nota,
          coordenadas: { lat: place.latitude, lng: place.longitude },
          gratis: !isPaidPlace(place),
          precio_estimado: isPaidPlace(place) ? estimatedCostEur(place) : 0,
          minutos_estimados: visitMinutesFor(place, opts.freeMinutes ?? 90, opts.transporte ?? "walk"),
          razon_de_seleccion: buildReason(place, scores, opts),
          categorias: categoriesOfPlace(place),
        } satisfies RankedPlanItem,
      };
    })
    .sort((a, b) => b.scores.nota - a.scores.nota || (a.place.distanceMeters || 0) - (b.place.distanceMeters || 0));

  // Con varias categorías marcadas, los mejores de cada una se alternan para que el top 20 no sea de una sola.
  const ordered = opts.categories && opts.categories.length > 1 ? interleaveRows(scored, opts.categories) : scored;
  const out: RankedPlanItem[] = [];
  const seen = new Set<string>();
  for (const row of ordered) {
    const key = `${row.item.nombre.toLowerCase()}|${row.item.coordenadas.lat.toFixed(4)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row.item);
    if (out.length >= limit) break;
  }
  return out;
}

function interleaveRows<T extends { place: NearbyPlace }>(rows: T[], categories: PlaceCategoryId[]): T[] {
  const byPlace = new Map(rows.map((r) => [r.place, r] as const));
  return interleaveByCategory(rows.map((r) => r.place), categories).map((p) => byPlace.get(p)!);
}

/**
 * Pipeline completo (pasos 1–6).
 * Ingesta masiva dentro de RadioMaximo, eventos, filtros, scoring, top 20.
 */
export async function searchPlansEngine(input: PlanSearchInput): Promise<PlanSearchResult> {
  const tiempoLibreHoras = tiempoLibreHorasFromInput(input);
  const freeMinutes = Math.round(tiempoLibreHoras * 60);
  const { tiempoIdaHoras, radioMaximoKm, radioMaximoMeters } = computeRadioMaximoKm(
    input.transporte,
    tiempoLibreHoras,
  );
  const tipo = tipoFromMood(input.mood, input.tipo);
  const budget = effectiveBudget(input);

  let places: NearbyPlace[] = input.places?.length
    ? input.places.map((p) => ({
        ...p,
        rating: normalizeBaseRating(p),
        paid: p.paid ?? isPaidPlace(p),
      }))
    : [];

  if (!places.length) {
    const fetched = await fetchNearbyPlacesCascade({
      latitude: input.lat,
      longitude: input.lng,
      radii: engineRadii(radioMaximoMeters),
    });
    places = fetched.places.map((p) => ({
      ...p,
      rating: normalizeBaseRating(p),
      paid: isPaidPlace(p),
    }));
  }

  // Recortar al radio máximo exacto
  places = places.filter((p) => (p.distanceMeters ?? 0) <= radioMaximoMeters * 1.05);

  let eventsAdded = 0;
  // Los eventos «inventados» por la IA solo si se piden expresamente: la IA no conoce la agenda real
  // y generaba conciertos/mercadillos que no existen.
  if (input.aiEvents) {
    const today = new Date().toISOString().slice(0, 10);
    const llmEvents = await fetchEventsViaLlm({ lat: input.lat, lng: input.lng, dateIso: today });
    for (const ev of llmEvents) {
      const dLat = ((ev.latitude - input.lat) * Math.PI) / 180;
      const dLon = ((ev.longitude - input.lng) * Math.PI) / 180;
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos((input.lat * Math.PI) / 180) * Math.cos((ev.latitude * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
      ev.distanceMeters = Math.round(2 * 6371000 * Math.asin(Math.sqrt(a)));
      if ((ev.distanceMeters || 0) <= radioMaximoMeters * 1.1) {
        places.push(ev);
        eventsAdded += 1;
      }
    }
  }

  const bySetting0 = applyBooleanExclusions(places, { setting: input.setting });
  // Filtro por categorías: solo pasan los sitios de alguna categoría marcada (sin marcar = todos).
  const categories = input.categories ?? [];
  const bySetting = filterPlacesByCategories(bySetting0, categories);
  const byBudget = bySetting.filter((p) => budget == null || fitsBudget(p, budget));
  const afterBoolean = byBudget.filter((p) => fitsTime(p, freeMinutes, input.transporte));

  // Sin «relleno» saltándose filtros: si el usuario pide interiores / X € / Y minutos, se respeta.
  const items = rankPlacesToOutput(afterBoolean, {
    tipo,
    social: input.social,
    kit: input.kit,
    limit: 20,
    freeMinutes,
    transporte: input.transporte,
    categories,
  });

  return {
    radioMaximoKm: round2(radioMaximoKm),
    tiempoIdaHoras: round2(tiempoIdaHoras),
    placesIngested: places.length,
    eventsAdded,
    afterFilters: afterBoolean.length,
    discardedByBudget: bySetting.length - byBudget.length,
    discardedByTime: byBudget.length - afterBoolean.length,
    discardedByCategory: bySetting0.length - bySetting.length,
    items: items.slice(0, 20),
  };
}

/** Radios (m) de la búsqueda en cascada para un RadioMaximo dado. */
export function engineRadii(radioMaximoMeters: number): number[] {
  return Array.from(
    new Set([
      Math.round(radioMaximoMeters * 0.35),
      Math.round(radioMaximoMeters * 0.65),
      radioMaximoMeters,
    ].filter((n) => n >= 800)),
  ).sort((a, b) => a - b);
}

/** Adapta el output del motor a PlanCard UI existente. */
export function rankedItemsToPlanCards(
  items: RankedPlanItem[],
  minutesBudget: number,
): Array<{
  title: string;
  detail: string;
  minutes: number;
  price?: number;
  map?: { lat: number; lon: number };
  placeName?: string;
  categorias?: string[];
}> {
  return items.map((item) => ({
    title: item.nombre,
    detail: `${item.tipo_actividad} · ★${item.puntuacion_calculada.toFixed(1)} · ${item.razon_de_seleccion}`,
    minutes: Math.max(10, Math.min(minutesBudget, item.minutos_estimados ?? 90)),
    price: item.precio_estimado ?? (item.gratis ? 0 : undefined),
    map: { lat: item.coordenadas.lat, lon: item.coordenadas.lng },
    placeName: item.nombre,
    categorias: item.categorias,
  }));
}
